import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Grid,
  Pagination,
  FormControlLabel,
  Switch,
  InputAdornment,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import config from "../../config";

const emptyForm = {
  varietyName: "",
  varietyCode: "",
  description: "",
  status: true,
};

const limit = 10;

export default function Variety() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [allVarieties, setAllVarieties] = useState([]);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const fetchVarieties = useCallback(async () => {
    try {
      setRefreshing(true);
      const token = sessionStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/variety/get`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        setAllVarieties(Array.isArray(data.data) ? data.data : []);
      } else {
        setAllVarieties([]);
      }
    } catch (error) {
      setAllVarieties([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchVarieties();
  }, [fetchVarieties]);

  const filteredVarieties = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return allVarieties.filter((variety) => {
      const matchesSearch = term
        ? (variety.varietyName || "").toLowerCase().includes(term) ||
          (variety.varietyCode || "").toLowerCase().includes(term)
        : true;

      const isActive = variety.status !== false;
      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "true"
          ? isActive
          : !isActive;

      return matchesSearch && matchesStatus;
    });
  }, [allVarieties, searchTerm, statusFilter]);

  const total = filteredVarieties.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const varieties = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredVarieties.slice(start, start + limit);
  }, [filteredVarieties, page]);

  const openAddForm = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    if (!formLoading) {
      setFormOpen(false);
      setFormData(emptyForm);
      setFormErrors({});
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.varietyName.trim()) {
      errors.varietyName = "Variety name is required";
    }
    if (!formData.varietyCode.trim()) {
      errors.varietyCode = "Variety code is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setFormLoading(true);
      const token = sessionStorage.getItem("token");

      const url = `${config.apiBaseUrl}/variety/create`;

      const payload = {
        varietyName: formData.varietyName.trim(),
        varietyCode: formData.varietyCode.trim(),
        description: formData.description.trim(),
        status: formData.status,
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.success !== false) {
        setSnackbar({
          open: true,
          message: data.message || "Variety added successfully",
          severity: "success",
        });

        setFormOpen(false);
        setFormData(emptyForm);

        await fetchVarieties();
        setPage(1);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Something went wrong",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openDeleteDialog = (variety) => {
    setSelectedVariety(variety);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteDialog(false);
      setSelectedVariety(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedVariety) return;

    try {
      setDeleteLoading(true);
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        `${config.apiBaseUrl}/variety/delete/${selectedVariety._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();

      setDeleteDialog(false);
      setSelectedVariety(null);

      if (res.ok) {
        setSnackbar({
          open: true,
          message: data.message || "Variety deleted successfully",
          severity: "success",
        });

        const wasLastOnPage = varieties.length === 1 && page > 1;
        await fetchVarieties();
        if (wasLastOnPage) {
          setPage((prev) => prev - 1);
        }
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to delete variety",
          severity: "error",
        });
      }
    } catch (error) {
      setDeleteDialog(false);
      setSelectedVariety(null);

      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusFilter("all");
    setPage(1);
  };

  const handleViewDetail = (variety) => {
    if (!variety?._id) return;
    navigate(`/variety-detail/${variety._id}`);
  };

  const varietyNameLinkSx = {
    color: "#2563EB",
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  };

    const desktopHeadCellSx = {
    px: 2,
    py: 1.5,
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "rgba(15,23,42,0.75)",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
    };

    const desktopBodyCellSx = {
    px: 2,
    py: 1.5,
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
    };

  const mobileHeadCellSx = {
    px: 1.25,
    py: 1,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.55)",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
  };
  const mobileBodyCellSx = {
    px: 1.25,
    py: 1.1,
    fontSize: "0.8rem",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden" }}>
        <Box sx={{ p: { xs: 2, md: 4 }, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mb={4}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <Typography variant="h4" fontWeight={800}>
                Varieties
              </Typography>

              {refreshing && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Stack>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openAddForm}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                },
              }}
            >
              Add Variety
            </Button>
          </Stack>

          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by variety name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput("")}>
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={clearFilters}
                  sx={{
                    height: 40,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ position: "relative" }}>
            {refreshing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(248,250,252,0.6)",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box
              sx={{
                opacity: refreshing ? 0.5 : 1,
                pointerEvents: refreshing ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >
              {isMobile ? (
                
                <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={mobileHeadCellSx}>Variety</TableCell>
                          <TableCell sx={mobileHeadCellSx}>Status</TableCell>
                          <TableCell sx={{ ...mobileHeadCellSx, borderRight: "none" }} align="center">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {varieties.length > 0 ? (
                          varieties.map((variety, idx) => {
                            const zebraSx = idx % 2 === 1 ? { background: "rgba(15,23,42,0.015)" } : {};
                            return (
                              <TableRow key={variety._id} sx={zebraSx}>
                                <TableCell sx={mobileBodyCellSx}>
                                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        onClick={() => handleViewDetail(variety)}
                                        sx={{ ...varietyNameLinkSx, fontSize: "0.82rem" }}
                                        noWrap
                                      >
                                        {variety.varietyName || "—"}
                                      </Typography>
                                      {variety.varietyCode && (
                                        <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.5)" }} noWrap>
                                          Code: {variety.varietyCode}
                                        </Typography>
                                      )}
                                    </Box>
                                    {variety.description && (
                                      <Tooltip title={variety.description}>
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: 15, color: "rgba(15,23,42,0.35)", mt: 0.25, flexShrink: 0 }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell sx={mobileBodyCellSx}>
                                  <Chip
                                    label={variety.status !== false ? "Active" : "Inactive"}
                                    size="small"
                                    color={variety.status !== false ? "success" : "default"}
                                    sx={{ height: 22, fontSize: "0.66rem", "& .MuiChip-label": { px: 0.9 } }}
                                  />
                                </TableCell>
                                <TableCell sx={{ ...mobileBodyCellSx, borderRight: "none" }} align="center">
                                  <Stack direction="row" spacing={0.25} justifyContent="center">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => openDeleteDialog(variety)}
                                    >
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 4, borderRight: "none" }}>
                              No varieties found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <Paper
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        maxWidth: "100%",
                    }}
                    >
                    <TableContainer
                        sx={{
                        overflowX: "auto",
                        maxWidth: "100%",
                        }}
                    >
                        <Table sx={{ minWidth: 780 }}>
                        <TableHead>
                            <TableRow>
                            <TableCell sx={desktopHeadCellSx}>
                                Variety Name
                            </TableCell>

                            <TableCell sx={desktopHeadCellSx}>
                                Variety Code
                            </TableCell>

                            <TableCell sx={desktopHeadCellSx}>
                                Description
                            </TableCell>

                            <TableCell sx={desktopHeadCellSx}>
                                Status
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                ...desktopHeadCellSx,
                                borderRight: "none",
                                }}
                            >
                                Actions
                            </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {varieties.length > 0 ? (
                            varieties.map((variety, idx) => {
                                const zebraSx =
                                idx % 2 === 1
                                    ? {
                                        background:
                                        "rgba(15,23,42,0.015)",
                                    }
                                    : {};

                                return (
                                <TableRow
                                    key={variety._id}
                                    sx={zebraSx}
                                >
                                    <TableCell sx={desktopBodyCellSx}>
                                    <Typography
                                        component="span"
                                        onClick={() => handleViewDetail(variety)}
                                        sx={varietyNameLinkSx}
                                    >
                                        {variety.varietyName || "—"}
                                    </Typography>
                                    </TableCell>

                                    <TableCell sx={desktopBodyCellSx}>
                                    {variety.varietyCode || "—"}
                                    </TableCell>

                                    <TableCell
                                    sx={{
                                        ...desktopBodyCellSx,
                                        maxWidth: 280,
                                    }}
                                    >
                                    {variety.description || "—"}
                                    </TableCell>

                                    <TableCell sx={desktopBodyCellSx}>
                                    <Chip
                                        label={
                                        variety.status !== false
                                            ? "Active"
                                            : "Inactive"
                                        }
                                        size="small"
                                        color={
                                        variety.status !== false
                                            ? "success"
                                            : "default"
                                        }
                                    />
                                    </TableCell>

                                    <TableCell
                                    align="center"
                                    sx={{
                                        ...desktopBodyCellSx,
                                        borderRight: "none",
                                    }}
                                    >
                                    <IconButton
                                        color="error"
                                        onClick={() =>
                                        openDeleteDialog(variety)
                                        }
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                    </TableCell>
                                </TableRow>
                                );
                            })
                            ) : (
                            <TableRow>
                                <TableCell
                                colSpan={5}
                                align="center"
                                sx={{
                                    py: 4,
                                    borderRight: "none",
                                }}
                                >
                                No varieties found
                                </TableCell>
                            </TableRow>
                            )}
                        </TableBody>
                        </Table>
                    </TableContainer>
                    </Paper>
              )}
              {total > 0 && totalPages > 1 && (
                <Stack alignItems="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    size={isMobile ? "small" : "medium"}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                  />
                  <Typography variant="caption" sx={{ color: "#6B7280", mt: 1 }}>
                    Page {page} of {totalPages} · {total} total
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={700}>Add Variety</DialogTitle>

        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Variety Name"
                  name="varietyName"
                  value={formData.varietyName}
                  onChange={handleFormChange}
                  required
                  autoFocus
                  error={Boolean(formErrors.varietyName)}
                  helperText={formErrors.varietyName || ""}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Variety Code"
                  name="varietyCode"
                  value={formData.varietyCode}
                  onChange={handleFormChange}
                  required
                  error={Boolean(formErrors.varietyCode)}
                  helperText={formErrors.varietyCode || ""}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    px: 2,
                    py: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Active"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                    labelPlacement="start"
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeForm} disabled={formLoading}>
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                },
              }}
            >
              {formLoading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Add Variety"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Variety</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{selectedVariety?.varietyName}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}